import React from 'react';
import { Paper } from '@mui/material';
import PropTypes from 'prop-types';
import * as echarts from 'echarts/core';
import {
  DataZoomComponent,
  LegendComponent,
  LegendScrollComponent,
  TimelineComponent,
  TitleComponent,
  TooltipComponent,
} from 'echarts/components';
import { BarChart, LineChart, PieChart } from 'echarts/charts';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import moment from 'moment/moment';
import { isEmpty, isEqual } from 'lodash';
import { useNavigate, Link } from 'react-router-dom';

import { getDefaultOptions } from '../../Reports/components/FaultsChart/chartOptions';
import { chartStyle } from '../../Reports/components/DrillDownChart/component';
import { datePickerFormat } from '../../../common/services/ValidationRules';
import ChartHeader from './ChartHeader/ChartHeader';

const BarChartComponent = ({ title, dataStatistic, path }) => {
  const navigate = useNavigate();

  echarts.use([
    PieChart,
    LineChart,
    TooltipComponent,
    BarChart,
    TitleComponent,
    LegendComponent,
    LegendScrollComponent,
    DataZoomComponent,
    TimelineComponent,
  ]);

  if (isEmpty(dataStatistic) || !dataStatistic.data) return;

  const handleClick = (params) => {
    if (params.componentType === 'series') {
      navigate(path);
    }
  };

  const totalAmount = dataStatistic.data.reduce((acc, entry) => {
    for (const key in entry) {
      if (key !== 'date') acc += entry[key];
    }

    return acc;
  }, 0);

  const option = {
    ...getDefaultOptions(),
    legend: {
      ...getDefaultOptions().legend,
      data: dataStatistic.legend.map((item) => item.label),
    },
    series: dataStatistic.legend.map((item) => ({
      ...getDefaultOptions().series[0],
      name: item.label,
      data: dataStatistic.data.map((entry) => entry[item.key]),
    })),
    tooltip: {
      ...getDefaultOptions().tooltip,
      formatter(params) {
        const dateValue = dataStatistic.data[params.dataIndex].date;

        return `${moment.unix(dateValue).utc().format(datePickerFormat)} <br /> ${params.seriesName} : ${params.data}`;
      },
    },
    xAxis: {
      ...getDefaultOptions().xAxis,
      axisLabel: {
        ...getDefaultOptions().xAxis.axisLabel,
        formatter: (value) => {
          const dateValue = dataStatistic.data[value].date;

          return moment.unix(dateValue).utc().format(datePickerFormat);
        },
      },
    },
  };

  // Here we change colors by default when we have only 2< legends
  if (dataStatistic.legend.length <= 2) {
    option.color = ['rgb(147, 204, 99)', 'rgb(69, 70, 72)'];
  }

  const chartEvents = {
    click: handleClick,
  };

  return (
    <Paper className="chart__paper">
      <ChartHeader>
        <h2>
          <Link to={path} className="link">
            {title}
          </Link>
        </h2>
        <div className="total__count">{`total ${totalAmount}`}</div>
      </ChartHeader>
      <ReactEChartsCore
        echarts={echarts}
        chartsStyle={chartStyle}
        notMerge
        silent
        lazyUpdate
        option={option}
        onEvents={chartEvents}
      />
    </Paper>
  );
};

BarChartComponent.propTypes = {
  title: PropTypes.string.isRequired,
  dataStatistic: PropTypes.object.isRequired,
  path: PropTypes.string.isRequired,
};

export default React.memo(
  BarChartComponent,
  (prevProps, nextProps) =>
    isEqual(prevProps.dataStatistic, nextProps.dataStatistic) &&
    isEqual(prevProps.title, nextProps.title) &&
    isEqual(prevProps.path, nextProps.path),
);
